import React, { useCallback, useContext, useEffect, useState } from 'react';
import ModalDialog, { ModalTransition } from '@atlaskit/modal-dialog';
import Form from '../../../../../kingpin/forms/Form';
import FormHeader from '../../../../../kingpin/forms/FormHeader';
import FormContent from '../../../../../kingpin/forms/FormContent';
import useGetUnusedMetrics from './useGetUnusedMetrics';
import { Link } from 'react-router-dom';
import { Scrollable } from '../../../../../components/UserPicker/UserPicker';
import Button from '../../../../../kingpin/atoms/Button';
import Row from '../../../../../components/Common/Row';
import Checkbox from '../../../../../components/Inputs/Checkbox';
import Typography from '../../../../../kingpin/atoms/Typography';
import Loading from '../../../../../components/Loading';
import useArchiveUnusedMetrics from './useArchiveUnusedMetrics';
import ToastContext from '../../../../../contexts/ToastContext';

const ArchiveUnusedMetrics = ({ close }: { close: () => void }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [unusedMetrics, setUnusedMetrics] = useState<
    {
      id: string;
      usageCount: number;
      name: string;
      link: string;
    }[]
  >([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const { showToast } = useContext(ToastContext);

  const getUnusedMetrics = useGetUnusedMetrics();
  const archiveUnusedMetrics = useArchiveUnusedMetrics();

  const toggleMetric = useCallback(
    (metricId: string) => () => {
      const newSelectedMetrics = [...selectedMetrics];
      if (newSelectedMetrics.includes(metricId)) {
        newSelectedMetrics.splice(newSelectedMetrics.indexOf(metricId), 1);
      } else {
        newSelectedMetrics.push(metricId);
      }
      setSelectedMetrics(newSelectedMetrics);
    },
    [selectedMetrics],
  );

  const archiveMetrics = useCallback(async () => {
    setIsLoading(true);
    await archiveUnusedMetrics(selectedMetrics);
    showToast(`${selectedMetrics.length} metrics archived`);
    close();
    setIsLoading(false);
  }, [archiveUnusedMetrics, selectedMetrics, showToast, close]);

  useEffect(() => {
    const unusedMetrics = getUnusedMetrics();
    setUnusedMetrics(unusedMetrics);
    setSelectedMetrics(unusedMetrics.map((metric) => metric.id));
  }, [getUnusedMetrics]);

  return (
    <>
      <Form>
        <FormHeader title={'Unused Metrics'} />
        {unusedMetrics.length === 0 && !isLoading && (
          <Typography.Header textAlign={'center'} type={'H3'}>
            No Unused Metrics found.
          </Typography.Header>
        )}
        {isLoading && <Loading />}
        {unusedMetrics.length > 0 && (
          <>
            <Typography.Body type={'Body 13'}>
              <strong>{selectedMetrics.length}</strong> of{' '}
              <strong>{unusedMetrics.length}</strong> selected
            </Typography.Body>
            <FormContent>
              <Scrollable
                style={{
                  maxHeight: 500,
                  border: '1px solid var(--color-border-tertiary)',
                  marginTop: 5,
                  padding: 4,
                }}
              >
                {unusedMetrics.map((metric) => (
                  <Row style={{ padding: 4, marginTop: 5 }} key={metric.id}>
                    <Checkbox
                      testId={`archive-unused-${metric.id}`}
                      isChecked={selectedMetrics.includes(metric.id)}
                      onToggled={toggleMetric(metric.id)}
                    />
                    <span style={{ paddingLeft: 10, marginBottom: 5 }}></span>
                    <Link
                      style={{ marginTop: -5 }}
                      target={'_blank'}
                      to={metric.link}
                    >
                      {metric.name}
                    </Link>
                  </Row>
                ))}
              </Scrollable>
            </FormContent>
          </>
        )}
        <Row spaceBetween>
          <Button onClick={close} type="Secondary" size="Small" label="Close" />
          <Button
            type="Primary"
            isDisabled={!selectedMetrics.length}
            onClick={archiveMetrics}
            testId={'archive-unused-metrics-button'}
            size="Small"
            label="Archive"
          />
        </Row>
      </Form>
    </>
  );
};

const Gate = ({ isOpen, close }: { isOpen: boolean; close: () => void }) => (
  <ModalTransition>
    {isOpen && (
      <ModalDialog
        width={450}
        onClose={close}
        shouldScrollInViewport={false}
        autoFocus={false}
      >
        <ArchiveUnusedMetrics close={close} />
      </ModalDialog>
    )}
  </ModalTransition>
);

export default Gate;
